import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useMsal } from "@azure/msal-react";
import { getAuthenticatedAccount } from "../utils/accountUtils";
import SearchField from "../components/SearchField";
import LoadingState from "../components/LoadingState";
import LoadMoreButton from "../components/LoadMoreButton";
import PageHeader from "../components/PageHeader";
import strings from "../strings";

const Library = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [nextLink, setNextLink] = useState(null);
  const { instance, accounts } = useMsal();
  const tenantId = process.env.REACT_APP_AUTH_TENENT_ID;

  const fetchDocuments = useCallback(async (url = null) => {
    try {
      setLoading(true);
      const account = await getAuthenticatedAccount(instance, accounts, tenantId);
      const response = await instance.acquireTokenSilent({
        scopes: [process.env.REACT_APP_AUTH_SP_SCOPE],
        account: account,
      });

      const graphApiUrl = process.env.REACT_APP_GRAPH_API_URL;
      const siteId = process.env.REACT_APP_SP_SITE_ID;
      const listUrl = process.env.REACT_APP_ENVIRONMENTAL_DOCUMENT_LIBRARY_LIST_URL;

      const apiUrl = url || `${graphApiUrl}/sites/${siteId}/${listUrl}?$expand=fields&$orderby=fields/Modified desc&$top=10`;
      
      const result = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${response.accessToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await result.json();
      setNextLink(data["@odata.nextLink"]);
      
      if (url) {
        setDocuments(prev => [...prev, ...data.value]);
      } else {
        setDocuments(data.value);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, tenantId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleLoadMore = () => {
    if (nextLink) {
      fetchDocuments(nextLink);
    }
  };

  const getFileNameFromUrl = (url) => {
    try {
      const decodedUrl = decodeURIComponent(url);
      return decodedUrl.split('/').pop();
    } catch (error) {
      return 'N/A';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (!doc) return false;
    const searchLower = searchQuery.toLowerCase();
    const fileName = getFileNameFromUrl(doc.webUrl);
    return fileName.toLowerCase().includes(searchLower);
  });

  return (
    <Box sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <PageHeader 
          title={strings.library.pageHeader.title}
          externalLink={strings.library.pageHeader.externalLink}
        />

        <Box sx={{ mt: 4 }}>
          <SearchField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={strings.library.search.placeholder}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.library.table.headers.fileName}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.library.table.headers.type}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.library.table.headers.created}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.library.table.headers.modified}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{strings.library.table.headers.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && documents.length === 0 ? (
                  <LoadingState colSpan={5} />
                ) : filteredDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {strings.library.table.noDocuments}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocuments.map((doc) => {
                    if (!doc) return null;
                    const fileName = getFileNameFromUrl(doc.webUrl);
                    return (
                      <TableRow key={doc.id}>
                        <TableCell>{fileName}</TableCell>
                        <TableCell>{doc.contentType?.name || strings.library.table.defaultType}</TableCell>
                        <TableCell>
                          {doc.createdDateTime ? new Date(doc.createdDateTime).toLocaleDateString() : strings.common.na}
                        </TableCell>
                        <TableCell>
                          {doc.lastModifiedDateTime ? new Date(doc.lastModifiedDateTime).toLocaleDateString() : strings.common.na}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            href={doc.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  }).filter(Boolean)
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {nextLink && (
            <LoadMoreButton onClick={handleLoadMore} loading={loading} />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Library;
